import { useEffect, useRef, useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import { FullPageLoader } from "@/components/FullPageState";
import { ApiError } from "@/lib/restClient";

function SessionQueries({ children, accessToken }: { children: ReactNode; accessToken?: string }) {
  const previousToken = useRef(accessToken);
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: (attempt, error) =>
              !(error instanceof ApiError && [401, 403].includes(error.status)) && attempt < 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  useEffect(() => {
    if (previousToken.current === accessToken) return;
    previousToken.current = accessToken;
    void client.invalidateQueries({
      predicate: ({ queryKey }) => queryKey[0] === "account" || queryKey[0] === "tenants",
    });
  }, [accessToken, client]);

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}

export function SessionQueryProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  if (auth.isLoading && !auth.activeNavigator)
    return <FullPageLoader label="Checking sign-in status" />;

  const identity = JSON.stringify([
    auth.settings.authority,
    auth.settings.client_id,
    auth.user?.profile.sub ?? null,
    auth.user?.profile.auth_time ?? null,
  ]);

  return (
    <SessionQueries key={identity} accessToken={auth.user?.access_token}>
      {children}
    </SessionQueries>
  );
}
